import { useExchangeMagicLinkToken } from "@/api/auth/useExchangeMagicLinkToken";
import { useAuth } from "@/auth/AuthContext";
import { Center, Loader, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export function MagicLinkPage() {
  const params = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { supabase } = useAuth();
  const exchangeMutation = useExchangeMagicLinkToken();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent infinite loop - only run once
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = params.token;

    if (!token) {
      notifications.show({
        title: "오류",
        message: "유효하지 않은 링크입니다.",
        color: "red",
      });
      navigate("/", { replace: true });
      return;
    }

    // Exchange our token and create a Supabase session
    const processAuth = async () => {
      try {
        const data = await exchangeMutation.mutateAsync({ token });

        const { error } = await supabase.auth.setSession({
          access_token: data.accessToken,
          refresh_token: data.refreshToken,
        });

        if (error) {
          setStatus("error");
          notifications.show({
            title: "로그인 실패",
            message: `인증에 실패했습니다: ${error.message}`,
            color: "red",
          });
          setTimeout(() => navigate("/", { replace: true }), 2000);
          return;
        }

        setStatus("success");
        notifications.show({
          title: "로그인 성공",
          message: "환영합니다!",
          color: "green",
        });

        const redirect = `${data.redirectPath}${location.hash}`;
        setTimeout(() => navigate(redirect, { replace: true }), 1000);
      } catch (error) {
        setStatus("error");
        notifications.show({
          title: "링크 오류",
          message:
            error instanceof Error ? error.message : "링크가 만료되었거나 유효하지 않습니다.",
          color: "red",
        });
        setTimeout(() => navigate("/", { replace: true }), 2000);
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Center style={{ height: "100vh", width: "100%" }}>
      <Stack align="center" gap="md">
        {status === "loading" && (
          <>
            <Loader size="lg" />
            <Text size="lg">로그인 중...</Text>
          </>
        )}
        {status === "success" && (
          <>
            <Text size="lg" c="green">
              ✓ 로그인 성공!
            </Text>
            <Text size="sm" c="dimmed">
              페이지로 이동 중...
            </Text>
          </>
        )}
        {status === "error" && (
          <>
            <Text size="lg" c="red">
              ✗ 로그인 실패
            </Text>
            <Text size="sm" c="dimmed">
              홈으로 돌아가는 중...
            </Text>
          </>
        )}
      </Stack>
    </Center>
  );
}
